import {
  DeliveryScheduleBundle,
  DeliveryScheduleBundleDoc,
} from '../../interfaces/delivery-schedule/delivery-schedule-bundle.interface';
import { BundleSyncService } from '../app-ui/bundle-sync.service';
import { DB_KEY_PREFIXES } from '../../config/pouch-db.config';
import { DELIVERY_SCHEDULE_BUNDLE_SCHEMA } from './delivery-schedule-bundle.schema';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { EntityNames } from '../delta/delta.service';
import { Injectable } from '@angular/core';

const DELIVERY_SCHEDULE_BUNDLE_ACTION = 'indskr_GetDeliveryScheduleBundle';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Delivery schedules from `indskr_GetDeliveryScheduleBundle`. The server returns the schedules
 * mapped to the rep's business unit plus the org default, which everyone gets, so there is one
 * projection per business unit and one call per sync. See docs/features/order-capture.md.
 */
@Injectable({ providedIn: 'root' })
export class DeliveryScheduleDataService {
  constructor(
    private readonly bundles: BundleSyncService,
    private readonly dynamics: DynamicsClientService,
  ) {}

  /** Key for one business-unit projection. No account, position or AsOf takes part. */
  public getProjectionKey(
    businessUnitId: string | null = this.bundles.businessUnitId(),
  ): string {
    return `${DB_KEY_PREFIXES.DELIVERY_SCHEDULE_BUNDLE}_${businessUnitId ?? 'none'}`;
  }

  /** Cached bundle for the rep's business unit, or null when never synced. */
  public async getCachedBundle(): Promise<DeliveryScheduleBundle | null> {
    return (
      (
        await this.bundles.cached<DeliveryScheduleBundleDoc>(
          this.getProjectionKey(),
        )
      )?.bundle ?? null
    );
  }

  /**
   * Syncs the business-unit projection; true when the device copy was replaced. Never gated on
   * positions: the server reads `businessunit`, and a rep with neither still gets the org default.
   */
  public async syncDeliveryScheduleBundle(
    loadFromDbOnly = false,
  ): Promise<boolean> {
    if (loadFromDbOnly || this.bundles.isOffline) return false;
    const businessUnitId = this.bundles.businessUnitId();
    if (!businessUnitId)
      console.warn(
        'DeliveryScheduleDataService: no business unit for user; only the org default schedule applies',
      );
    const key = this.getProjectionKey(businessUnitId);
    const replaced = await this.bundles.sync<
      DeliveryScheduleBundle,
      DeliveryScheduleBundleDoc
    >({
      action: DELIVERY_SCHEDULE_BUNDLE_ACTION,
      entityName: EntityNames.deliveryScheduleBundle,
      key,
      rawData: () => ({
        subjects: this.bundles.subjects(this.bundles.positionIds()),
        scope: 'all',
      }),
      stored: { businessUnitId },
      schema: DELIVERY_SCHEDULE_BUNDLE_SCHEMA,
      enrich: async (bundle, cached) => ({
        ...bundle,
        orgDefaultId: await this.orgDefaultId(
          cached?.bundle?.orgDefaultId ?? null,
        ),
      }),
      count: (bundle) => bundle.deliverySchedules?.length ?? 0,
      afterWrite: (bundle) => {
        const unusable = (bundle.deliverySchedules ?? []).filter(
          (s) => !s.id || s.id === EMPTY_GUID,
        ).length;
        if (unusable > 0)
          console.warn(
            `DeliveryScheduleDataService: ${unusable} schedules returned an empty GUID; their selection cannot be saved`,
          );
      },
    });
    // A move to another business unit, or the older per-position copies, would otherwise stay forever.
    if (replaced) await this.purgeOrphanedBundles(key);
    return replaced;
  }

  /** Drops every cached projection but the current one. */
  public async purgeOrphanedBundles(
    keep: string = this.getProjectionKey(),
  ): Promise<void> {
    return this.bundles.purge(
      DB_KEY_PREFIXES.DELIVERY_SCHEDULE_BUNDLE,
      new Set([keep]),
    );
  }

  /**
   * Read here rather than with the org settings at sign-in: a tenant without the column would fail
   * that shared query, and sign-in with it. A failed read keeps what the device had.
   */
  private async orgDefaultId(previous: string | null): Promise<string | null> {
    try {
      const found = await this.dynamics.retrieveAll<{
        // eslint-disable-next-line @typescript-eslint/naming-convention -- a Dynamics lookup column
        _indskr_deliveryschedule_value?: string | null;
      }>('indskr_organizationsettings', ['_indskr_deliveryschedule_value']);
      return found?.value?.[0]?._indskr_deliveryschedule_value ?? null;
    } catch (error) {
      console.warn(
        'DeliveryScheduleDataService: org default schedule not refreshed',
        error,
      );
      return previous;
    }
  }
}
